import { DisclosureCollectionProps, DisclosureWithTitleProps } from 'libs/contentful/props';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import DisclosureCollectionTableOfContents from './DisclosureCollectionTableOfContents';

/**
 * Generates a URL-safe anchor ID from a title
 */
const generateAnchorId = (title: string, index: number): string => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'item';
  return `disclosure-${index + 1}-${slug}`;
};

/**
 * DisclosureCollection renders multiple Disclosure entries as a standalone module
 * in the main content area. Each disclosure can have its own title and copy.
 * Includes a collapsible table of contents for navigation.
 */
const DisclosureCollection = ({ disclosuresCollection, tableOfContentsExpanded }: DisclosureCollectionProps) => {
  const disclosures = (disclosuresCollection?.items ?? []).filter(Boolean);

  // Filter disclosures to only include those with valid copy content
  const disclosuresWithContent = disclosures.filter(
    disclosure => disclosure.copy?.json || disclosure.disclosureItem?.copy?.json
  );

  if (disclosuresWithContent.length === 0) {
    return null;
  }

  // Generate TOC items server-side
  const tocItems = disclosuresWithContent
    .map((disclosure, index) => ({
      label: disclosure.title ?? '',
      anchor: generateAnchorId(disclosure.title ?? `item-${index}`, index),
    }))
    .filter(item => item.label);

  return (
    <section className="content-wrapper py-4">
      <div className="max-w-[765px]">
        {/* Table of Contents - Client Component for interactivity */}
        {tocItems.length > 0 && (
          <DisclosureCollectionTableOfContents
            items={tocItems}
            defaultExpanded={tableOfContentsExpanded ?? true}
          />
        )}

        {/* Disclosure Items */}
        <ol className="space-y-12 list-decimal list-inside">
          {disclosuresWithContent.map((disclosure, index) => (
            <DisclosureItem
              key={disclosure.sys?.id ?? index}
              anchorId={generateAnchorId(disclosure.title ?? `item-${index}`, index)}
              {...disclosure}
            />
          ))}
        </ol>
      </div>
    </section>
  );
};

interface DisclosureItemProps extends DisclosureWithTitleProps {
  anchorId: string;
}

const DisclosureItem = ({ anchorId, title, copy, disclosureItem }: DisclosureItemProps) => {
  // Use copy field if available, otherwise fall back to legacy disclosureItem
  const copyContent = copy || disclosureItem?.copy;

  if (!copyContent?.json) {
    return null;
  }

  return (
    <li id={anchorId} className="font-sans text-heading-sm md:text-heading-md font-semibold scroll-mt-24">
      {title && <h2 className="inline">{title}</h2>}
      <div className="mt-4 font-normal text-base">
        <RichTextCopy copy={copyContent} />
      </div>
    </li>
  );
};

export default DisclosureCollection;
